import {

  CAPABILITY_VERIFICATION_STATUS,

  DIAGNOSTIC_CAPABILITY_OPTIONS,

  getEffectiveDiagnosticCapabilities,

} from "../../constants/diagnosticCapabilities";

import {
  FACILITY_TYPE,
  isEmergencyDepartment,
  isUrgentCare,
} from "../../constants/facilityTypes";
import {
  isLocationEligibleForMatching,
  normalizePatientAge,
} from "./facilityMatchEligibility";

/** Max facilities shown per type group in SCMAP triage panel. */
export const MAX_FACILITIES_PER_TYPE = 2;



// MATCH_TIER_HUE and MATCH_TIER_CHIP_COLORS must stay semantically aligned.
// When changing one, update the other. Current mapping:
//   full    → hue 0.36 (yellow-green pin)  ↔  chip bg #e8f5e9 (green)
//   partial → hue 0.13 (red-orange pin)    ↔  chip bg #fff8e1 (amber)
//   limited → hue 0.07 (red pin)           ↔  chip bg #fbe9e7 (orange-red)
//   none    → hue null (falls back to quality hue; no dedicated "no match" color)  ↔  chip bg #f5f5f5 (gray)
// Both constants are module-private; public surfaces are matchTierHue (on match results)
// and getMatchTierChipColor(tier) (for UI chip colors).

/** @type {Record<string, number | null>} Hue overrides for SvgMarker (0–1 scale). */

const MATCH_TIER_HUE = Object.freeze({

  full: 0.36,

  partial: 0.13,

  limited: 0.07,

  none: null,

});



/** @type {Record<string, { bg: string, text: string, border: string }>} */

const MATCH_TIER_CHIP_COLORS = Object.freeze({

  full: { bg: "#e8f5e9", text: "#1b5e20", border: "#2e7d32" },

  partial: { bg: "#fff8e1", text: "#7c4d00", border: "#f57f17" },

  limited: { bg: "#fbe9e7", text: "#7c2200", border: "#e65100" },

  none: { bg: "#f5f5f5", text: "#424242", border: "#9e9e9e" },

});



/**

 * @param {number} matchScore

 * @param {number} requiredCapabilitiesCount

 * @returns {'full' | 'partial' | 'limited' | 'none'}

 */

function computeMatchTier(matchScore, requiredCapabilitiesCount) {

  if (matchScore === 0 || requiredCapabilitiesCount === 0) return "none";

  if (matchScore === requiredCapabilitiesCount && requiredCapabilitiesCount > 0) {

    return "full";

  }

  const ratio = matchScore / requiredCapabilitiesCount;

  if (ratio >= 0.5) return "partial";

  if (matchScore > 0) return "limited";

  return "none";

}



/**

 * Rank already-loaded location docs by how many of the patient's diagnostic needs they cover.

 * Purely client-side; version-agnostic (works identically for SCMAP_SC_VERSION 'v2' or 'v4').

 *

 * @param {string[]} diagnosticNeeds - capability keys from the triage result.

 * @param {Array<object>} locationDocs - location documents (each may have diagnosticCapabilities[]).

 * @param {{ patientAge?: number | string | null }} [options]

 * @returns {Array<object>} locationDocs annotated with matchScore/matchedCapabilities/missingCapabilities,

 *   sorted by descending matchScore.

 */

export function matchFacilitiesToDiagnosticNeeds(diagnosticNeeds, locationDocs, options = {}) {

  const ageContext = normalizePatientAge(options.patientAge);



  const docs = Array.isArray(locationDocs)

    ? locationDocs.filter((locationDoc) => isLocationEligibleForMatching(locationDoc, ageContext))

    : [];

  if (!diagnosticNeeds?.length || !docs?.length) return docs;

  const needsCopy = [...diagnosticNeeds];

  return docs

    .map((loc) => {

      const caps = getEffectiveDiagnosticCapabilities(loc);

      const matched = diagnosticNeeds.filter((n) => caps.includes(n));

      const missing = diagnosticNeeds.filter((n) => !caps.includes(n));

      const requiredCapabilitiesCount = diagnosticNeeds.length;

      const matchScore = matched.length;

      const matchTier = computeMatchTier(matchScore, requiredCapabilitiesCount);

      const pediatricAgeUnknown = Boolean(loc.pediatricOnly && ageContext.isUnknown);
      // PERMANENT DECISION (do not re-litigate): an Urgent Care with empty
      // diagnosticCapabilities scores ZERO against diagnostic needs. isAssumedDefault
      // is a UI-only "typical services" flag and must NEVER inflate matchScore.
      const isAssumedDefault =
        isUrgentCare(loc) &&
        (!Array.isArray(loc.diagnosticCapabilities) ||
          loc.diagnosticCapabilities.length === 0);

      return {
        ...loc,
        capabilityVerificationStatus:
          loc?.capabilityVerification?.status || CAPABILITY_VERIFICATION_STATUS.UNVERIFIED,
        requiredCapabilitiesCount,
        matchScore,
        matchedCapabilities: matched,
        missingCapabilities: missing,
        matchTier,
        matchTierHue: MATCH_TIER_HUE[matchTier],
        patientNeeds: needsCopy,
        pediatricAgeUnknown,
        effectiveCaps: caps,
        isAssumedDefault,
      };

    })

    .sort((a, b) => b.matchScore - a.matchScore);

}



export function getCapabilityLabel(key) {

  return DIAGNOSTIC_CAPABILITY_OPTIONS.find((o) => o.key === key)?.label ?? key;

}



/**

 * Patient-facing label for a diagnostic match tier (informational, not prescriptive).

 *

 * @param {'full' | 'partial' | 'limited' | 'none'} tier

 * @param {number} matchScore

 * @param {number} totalNeeds

 * @returns {string}

 */

export function getMatchTierLabel(tier, matchScore, totalNeeds) {

  switch (tier) {

    case "full":

      return "Covers all currently suggested tests";

    case "partial":

    case "limited":

      return `Offers ${matchScore} of ${totalNeeds} suggested tests`;

    case "none":

    default:

      return "Diagnostic capabilities not on file";

  }

}



/**

 * MUI chip / sx colors for match tier badges (not used by map markers).

 *

 * @param {'full' | 'partial' | 'limited' | 'none'} tier

 * @returns {{ bg: string, text: string, border: string }}

 */

export function getMatchTierChipColor(tier) {
  return MATCH_TIER_CHIP_COLORS[tier] || MATCH_TIER_CHIP_COLORS.none;
}

/**
 * Resolve which care-type bucket a facility belongs in (ED → Urgent Care → Clinic).
 *
 * @param {object} facility
 * @returns {'ed' | 'urgent_care' | 'clinic'}
 */
function resolveFacilityTypeBucket(facility) {
  if (isEmergencyDepartment(facility)) return "ed";
  if (isUrgentCare(facility)) return "urgent_care";
  return "clinic";
}

/**
 * Splits a flat sorted array of matched facilities into typed groups,
 * preserving the within-group match-score order.
 * Returns groups in care-urgency order: EDs first, Urgent Cares second, Clinics third.
 *
 * @param {Array<object>} facilities
 * @returns {Array<{ typeKey: string, typeLabel: string, facilities: Array<object> }>}
 */
export function groupFacilitiesByType(facilities) {
  const groups = [
    {
      typeKey: FACILITY_TYPE.EMERGENCY_DEPARTMENT,
      typeLabel: "Emergency Departments",
      facilities: [],
    },
    {
      typeKey: "urgent_care",
      typeLabel: "Urgent Care Centers",
      facilities: [],
    },
    {
      typeKey: FACILITY_TYPE.CLINIC,
      typeLabel: "Clinics",
      facilities: [],
    },
  ];

  const bucketIndex = { ed: 0, urgent_care: 1, clinic: 2 };

  for (const facility of facilities) {
    const bucket = resolveFacilityTypeBucket(facility);
    groups[bucketIndex[bucket]].facilities.push(facility);
  }

  return groups.filter((group) => group.facilities.length > 0);
}


